// ---------------------------------------------------------------------------
// DnD Email Editor – Block Definitions
//
// Each block describes a palette item that can be dragged to create a layer
// or element. The `tier` field determines where the block can be dropped.
// ---------------------------------------------------------------------------

import type { LucideIcon } from 'lucide-react';
import {
  Columns3,
  MoveVertical,
  Minus,
  Type,
  ImageIcon,
  RectangleHorizontal,
  Code,
  Share2,
  PlayCircle,
  Clock,
  PanelTop,
  PanelBottom,
  Blocks,
} from 'lucide-react';
import type { LayerType, ElementType, Layer } from './dnd-model';

export type BlockTier = 'layer' | 'element' | 'both';

export type BlockCategory = 'structure' | 'content' | 'advanced' | 'saved';

/**
 * A saved block resolves to a set of pre-built layers supplied to the editor
 * (via `DndEditorProps.savedBlocks`) rather than a single primitive `type`.
 *
 * `header` / `footer` are the two reserved refs for the account defaults in
 * marketing settings — they get special insert-position snapping. Everything
 * else is a user-authored Email Component keyed by its row id.
 */
export type SavedBlockRef = 'header' | 'footer' | `component-${number}`;

/**
 * One entry in the editor's saved-block library. The editor is handed an ordered
 * array of these: the two account defaults plus every Email Component.
 *
 * Header and footer each get their own palette tile (they're single, fixed
 * things). Components deliberately do NOT — however many exist, the palette
 * shows one "Components" tile that opens a picker, so the palette can't be
 * flooded and the user gets a preview before committing.
 */
export interface SavedBlockEntry {
  ref: SavedBlockRef;
  label: string;
  description?: string;
  layers: Layer[];
  /**
   * Compiled HTML, used only for the picker's thumbnail and preview pane. What
   * actually gets inserted is always `layers`. Absent for header/footer, which
   * have no picker.
   */
  previewHtml?: string;
}

/** True for a component entry (as opposed to the header/footer defaults). */
export function isComponentRef(ref: SavedBlockRef): boolean {
  return ref !== 'header' && ref !== 'footer';
}

export interface BlockDef {
  id: string;
  type: LayerType | ElementType;
  label: string;
  icon: LucideIcon;
  category: BlockCategory;
  tier: BlockTier;
  description: string;
  /**
   * When true the block stays fully wired (type, compiler, canvas renderer,
   * defaults, existing content) but is NOT offered in the palette. Used to
   * retire a block without deleting its code so it can be re-enabled later.
   */
  hidden?: boolean;
  /**
   * Marks a "saved" block whose content is a set of pre-built layers resolved
   * at insert time from the matching `savedBlocks` entry. When set, insertion
   * branches on this ref instead of `type` (a nominal placeholder here).
   */
  savedRef?: SavedBlockRef;
  /**
   * Marks the single "Components" tile. Instead of inserting anything directly,
   * dragging or clicking it opens the component picker; the insert happens when
   * the user confirms a choice there.
   */
  opensComponentPicker?: boolean;
}

export const BLOCK_DEFINITIONS: BlockDef[] = [
  // ---- Structure ----------------------------------------------------------
  {
    id: 'block-columns',
    type: 'columns',
    label: 'Columns',
    icon: Columns3,
    category: 'structure',
    tier: 'layer',
    description: 'Multi-column layout with flexible ratios',
  },
  {
    id: 'block-spacer',
    type: 'spacer',
    label: 'Spacer',
    icon: MoveVertical,
    category: 'structure',
    tier: 'both',
    description: 'Vertical space between layers',
  },
  {
    id: 'block-divider',
    type: 'divider',
    label: 'Divider',
    icon: Minus,
    category: 'structure',
    tier: 'both',
    description: 'Horizontal divider line',
  },

  // ---- Content ------------------------------------------------------------
  {
    id: 'block-text',
    type: 'text',
    label: 'Text',
    icon: Type,
    category: 'content',
    tier: 'both',
    description: 'Rich text with formatting and merge tags',
  },
  {
    id: 'block-image',
    type: 'image',
    label: 'Image',
    icon: ImageIcon,
    category: 'content',
    tier: 'both',
    description: 'Image with optional link',
  },
  {
    id: 'block-button',
    type: 'button',
    label: 'Button',
    icon: RectangleHorizontal,
    category: 'content',
    tier: 'both',
    description: 'Call-to-action button',
  },

  // ---- Advanced -----------------------------------------------------------
  {
    id: 'block-html',
    type: 'html',
    label: 'HTML',
    icon: Code,
    category: 'advanced',
    tier: 'both',
    description: 'Custom HTML block (sandboxed)',
  },
  {
    id: 'block-social',
    type: 'social',
    label: 'Social Links',
    icon: Share2,
    category: 'advanced',
    tier: 'both',
    description: 'Social media icon links',
  },
  {
    id: 'block-video',
    type: 'video',
    label: 'Video',
    icon: PlayCircle,
    category: 'advanced',
    tier: 'layer',
    description: 'Video thumbnail with play button',
  },
  {
    id: 'block-countdown',
    type: 'countdown',
    label: 'Countdown',
    icon: Clock,
    category: 'advanced',
    tier: 'layer',
    description: 'Countdown timer to a target date',
    // Hidden until a real countdown-GIF provider is integrated (email can't run
    // JS). Code is kept intact so it can be re-enabled by removing this flag.
    hidden: true,
  },

  // ---- Saved (account defaults + component library) -----------------------
  {
    id: 'block-saved-header',
    // `type` is a nominal placeholder — saved blocks insert layers via savedRef.
    type: 'html',
    label: 'Header',
    icon: PanelTop,
    category: 'saved',
    tier: 'layer',
    description: 'Your account default email header',
    savedRef: 'header',
  },
  {
    id: 'block-saved-footer',
    type: 'html',
    label: 'Footer',
    icon: PanelBottom,
    category: 'saved',
    tier: 'layer',
    description: 'Your account default email footer',
    savedRef: 'footer',
  },
  {
    // ONE tile for the whole component library, however many there are. It has
    // no savedRef: dragging or clicking it opens the picker, and the chosen
    // component is inserted from there.
    id: 'block-saved-components',
    type: 'html',
    label: 'Components',
    icon: Blocks,
    category: 'saved',
    tier: 'layer',
    description: 'Browse your saved email components and insert one',
    opensComponentPicker: true,
  },
];

/** The single palette tile that opens the component picker. */
export const SAVED_COMPONENTS_BLOCK_ID = 'block-saved-components';

export function getBlocksByCategory(category: BlockCategory): BlockDef[] {
  return BLOCK_DEFINITIONS.filter((b) => !b.hidden && b.category === category);
}

export function getBlocksByTier(tier: BlockTier): BlockDef[] {
  return BLOCK_DEFINITIONS.filter(
    (b) => !b.hidden && (b.tier === tier || b.tier === 'both'),
  );
}

export function getBlockById(id: string): BlockDef | undefined {
  return BLOCK_DEFINITIONS.find((b) => b.id === id);
}

export function getBlockByType(type: string): BlockDef | undefined {
  return BLOCK_DEFINITIONS.find((b) => b.type === type);
}

export const BLOCK_CATEGORIES: { key: BlockCategory; label: string }[] = [
  { key: 'structure', label: 'Structure' },
  { key: 'content', label: 'Content' },
  { key: 'advanced', label: 'Advanced' },
  { key: 'saved', label: 'Saved' },
];
